import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Heart, Menu, Search, ShoppingBag, X } from "lucide-react";
import { useShop } from "@/context/ShopContext";

const NAV = [
  { to: "/collections", label: "Collections" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const Logo = ({ dark = false }) => (
  <span className="inline-flex flex-col items-center leading-none">
    <span className={`font-serif text-2xl md:text-[1.7rem] tracking-[0.28em] font-medium ${dark ? "text-ivory" : "text-espresso"}`}>
      LAMSAH
    </span>
    <span className={`font-arabic text-sm mt-1 ${dark ? "text-ivory/70" : "text-taupe"}`} lang="ar" dir="rtl">
      لمسة
    </span>
  </span>
);

export default function Header() {
  const { cartCount, wishlist, meta, settings } = useShop();
  const comingSoon = (meta.categories || []).filter((c) => c.status === "coming_soon");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {settings?.announcement_enabled && settings?.announcement_text && (
        <div
          data-testid="announcement-bar"
          className="bg-espresso text-ivory text-center text-[10px] sm:text-[11px] uppercase tracking-[0.2em] py-2.5 px-4"
        >
          {settings.announcement_text}
        </div>
      )}
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 bg-ivory/95 backdrop-blur-md transition-shadow duration-500 ${
          scrolled ? "border-b border-beige" : "border-b border-transparent"
        }`}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <button
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="md:hidden p-2 -ml-2 text-espresso"
            >
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <Link to="/" data-testid="header-logo" className="hidden md:block" aria-label="LAMSAH home">
              <Logo />
            </Link>
            <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
              <div className="relative group">
                <NavLink
                  to="/shop"
                  data-testid="nav-shop"
                  className={({ isActive }) =>
                    `text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                      isActive ? "text-espresso" : "text-taupe hover:text-espresso"
                    }`
                  }
                >
                  Shop
                </NavLink>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <div className="bg-ivory border border-beige px-7 py-6 w-[620px] shadow-sm">
                    <Link to="/shop" data-testid="nav-shop-hijabs" className="block text-xs uppercase tracking-[0.2em] text-espresso hover:text-rose transition-colors">
                      All Hijabs
                    </Link>
                    <div className="grid grid-cols-3 gap-8 mt-5">
                      <div>
                        <p className="text-[9px] uppercase tracking-[0.25em] text-taupe mb-3">Collections</p>
                        <div className="space-y-2">
                          {(meta.collections || []).map((c) => (
                            <Link
                              key={c.slug}
                              to={`/collections/${c.slug}`}
                              data-testid={`nav-collection-${c.slug}`}
                              className="block text-[11px] uppercase tracking-[0.12em] text-espresso/75 hover:text-rose transition-colors"
                            >
                              {c.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-[9px] uppercase tracking-[0.25em] text-taupe mb-3">Shop by Colour</p>
                        <div className="space-y-2">
                          {(meta.families || []).map((f) => (
                            <Link
                              key={f.name}
                              to={`/colours/${f.slug}`}
                              data-testid={`nav-colour-${slug(f.name)}`}
                              className="block text-[11px] uppercase tracking-[0.12em] text-espresso/75 hover:text-rose transition-colors"
                            >
                              {f.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-[9px] uppercase tracking-[0.25em] text-taupe mb-3">Shop by Fabric</p>
                        <div className="space-y-2">
                          {(meta.fabrics || []).map((f) => (
                            <Link
                              key={f}
                              to={`/shop?fabric=${encodeURIComponent(f)}`}
                              data-testid={`nav-fabric-${slug(f)}`}
                              className="block text-[11px] uppercase tracking-[0.12em] text-espresso/75 hover:text-rose transition-colors"
                            >
                              {f}
                            </Link>
                          ))}
                          <Link
                            to="/fabric-guide"
                            data-testid="nav-fabric-guide"
                            className="block text-[11px] uppercase tracking-[0.12em] text-rose hover:text-espresso transition-colors pt-1"
                          >
                            Fabric Guide →
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-beige mt-6 pt-5 space-y-3">
                      {comingSoon.map((c) => (
                        <Link
                          key={c.id}
                          to={`/shop?category=${c.id}`}
                          data-testid={`nav-shop-${c.id}`}
                          className="flex items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-taupe hover:text-espresso transition-colors"
                        >
                          {c.label}
                          <span className="text-[9px] uppercase tracking-[0.15em] text-rose/90 border border-rose/40 px-1.5 py-0.5">Soon</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                      isActive ? "text-espresso" : "text-taupe hover:text-espresso"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <Link
            to="/"
            data-testid="header-logo-mobile"
            aria-label="LAMSAH home"
            className="md:hidden absolute left-1/2 -translate-x-1/2"
          >
            <Logo />
          </Link>

          <div className="flex items-center gap-1 sm:gap-2">
            <Link to="/search" data-testid="header-search-link" aria-label="Search" className="p-2.5 text-espresso hover:text-rose transition-colors duration-300">
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </Link>
            <Link to="/wishlist" data-testid="header-wishlist-link" aria-label="Wishlist" className="relative p-2.5 text-espresso hover:text-rose transition-colors duration-300">
              <Heart className="w-5 h-5" strokeWidth={1.5} />
              {wishlist.length > 0 && (
                <span data-testid="wishlist-count" className="absolute top-0.5 right-0 w-4 h-4 rounded-full bg-rose text-ivory text-[10px] flex items-center justify-center">
                  {wishlist.length}
                </span>
              )}
            </Link>
            <Link to="/cart" data-testid="header-cart-link" aria-label="Shopping bag" className="relative p-2.5 text-espresso hover:text-rose transition-colors duration-300">
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              {cartCount > 0 && (
                <span data-testid="cart-count" className="absolute top-0.5 right-0 w-4 h-4 rounded-full bg-espresso text-ivory text-[10px] flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      <div
        data-testid="mobile-menu-overlay"
        className={`fixed inset-0 z-50 bg-espresso/40 transition-opacity duration-300 md:hidden ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
      />
      <aside
        data-testid="mobile-menu"
        className={`fixed top-0 left-0 bottom-0 z-50 w-[85%] max-w-xs bg-ivory flex flex-col transition-transform duration-500 ease-out md:hidden ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between px-6 h-16 border-b border-beige">
          <Logo />
          <button data-testid="mobile-menu-close" aria-label="Close menu" onClick={() => setOpen(false)} className="p-2 text-espresso">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <nav className="flex flex-col px-6 py-8 gap-6 overflow-y-auto" aria-label="Mobile">
          <NavLink to="/" data-testid="mobile-nav-home" className="font-serif text-2xl text-espresso">Home</NavLink>
          <div>
            <NavLink to="/shop" data-testid="mobile-nav-shop" className="font-serif text-2xl text-espresso">Shop</NavLink>
            <div className="flex flex-col gap-5 mt-3 pl-4 border-l border-beige">
              <NavLink to="/shop" data-testid="mobile-nav-hijabs" className="text-sm uppercase tracking-[0.2em] text-espresso/80">All Hijabs</NavLink>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-taupe mb-2.5">Collections</p>
                <div className="flex flex-wrap gap-2">
                  {(meta.collections || []).map((c) => (
                    <NavLink
                      key={c.slug}
                      to={`/collections/${c.slug}`}
                      data-testid={`mobile-nav-collection-${c.slug}`}
                      className="text-[11px] uppercase tracking-[0.12em] text-espresso/75 border border-beige px-2.5 py-1.5"
                    >
                      {c.name}
                    </NavLink>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-taupe mb-2.5">By Colour</p>
                <div className="flex flex-wrap gap-2">
                  {(meta.families || []).map((f) => (
                    <NavLink
                      key={f.name}
                      to={`/colours/${f.slug}`}
                      data-testid={`mobile-nav-colour-${slug(f.name)}`}
                      className="text-[11px] uppercase tracking-[0.12em] text-espresso/75 border border-beige px-2.5 py-1.5"
                    >
                      {f.name}
                    </NavLink>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-taupe mb-2.5">By Fabric</p>
                <div className="flex flex-wrap gap-2">
                  {(meta.fabrics || []).map((f) => (
                    <NavLink
                      key={f}
                      to={`/shop?fabric=${encodeURIComponent(f)}`}
                      data-testid={`mobile-nav-fabric-${slug(f)}`}
                      className="text-[11px] uppercase tracking-[0.12em] text-espresso/75 border border-beige px-2.5 py-1.5"
                    >
                      {f}
                    </NavLink>
                  ))}
                </div>
                <NavLink
                  to="/fabric-guide"
                  data-testid="mobile-nav-fabric-guide"
                  className="inline-block text-[11px] uppercase tracking-[0.12em] text-rose mt-3"
                >
                  Fabric Guide →
                </NavLink>
              </div>
              {comingSoon.map((c) => (
                <NavLink
                  key={c.id}
                  to={`/shop?category=${c.id}`}
                  data-testid={`mobile-nav-${c.id}`}
                  className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-taupe"
                >
                  {c.label}
                  <span className="text-[9px] uppercase tracking-[0.15em] text-rose/90 border border-rose/40 px-1.5 py-0.5">Soon</span>
                </NavLink>
              ))}
            </div>
          </div>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-serif text-2xl text-espresso"
            >
              {n.label}
            </NavLink>
          ))}
          <NavLink to="/wishlist" data-testid="mobile-nav-wishlist" className="font-serif text-2xl text-espresso">Wishlist</NavLink>
          <NavLink to="/cart" data-testid="mobile-nav-shopping-bag" className="font-serif text-2xl text-espresso">Shopping Bag</NavLink>
        </nav>
        <div className="mt-auto px-6 py-8 border-t border-beige">
          <p className="overline-label">لمسة — Every Shade. Your Style.</p>
        </div>
      </aside>
    </>
  );
}
